import { renderMarkdownInline } from './markdown'

export interface TranscriptLine {
  /** Seconds into the recording, or null for a line with no cue. */
  at: number | null
  /** The name before the colon, if the line has one. */
  speaker: string | null
  /** The rest of the line, as sanitized inline HTML. */
  html: string
  /** Tags stripped, entities decoded — what search matches against. */
  text: string
}

/**
 * `[1:07] **Max:** text` — the shape other/transcripting produces. Both the
 * timestamp and the speaker are optional so a hand-typed transcript still
 * parses; a line missing both is just text, which is why `at` is nullable
 * rather than defaulted to zero.
 */
const LINE = /^\[?((?:(\d{1,2}):)?(\d{1,2}):([0-5]\d))\]?[ \t]*(?:\*\*([^*\n]+?):\*\*[ \t]*)?([\s\S]*)$/

const toSeconds = (h: string | undefined, m: string, s: string) =>
  Number(h ?? 0) * 3600 + Number(m) * 60 + Number(s)

/**
 * Split a transcript into addressable lines.
 *
 * Rendering line-by-line rather than as one blob of HTML is what makes the rest
 * possible: a line can be highlighted while the audio is inside it, scrolled to,
 * or hidden behind the preview. None of that is reachable through
 * dangerouslySetInnerHTML.
 */
export function parseTranscript(markdown: string): TranscriptLine[] {
  return (markdown ?? '')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const match = LINE.exec(block)
      if (!match) {
        const html = renderMarkdownInline(block, { timestamps: true })
        return { at: null, speaker: null, html, text: stripTags(html) }
      }
      const [, , h, m, s, speaker, rest] = match
      const name = speaker?.trim() || null
      // Timestamps stay linked inside the text: a line can reference another
      // moment, and those should still seek.
      const html = renderMarkdownInline(rest.trim(), { timestamps: true })
      return {
        at: toSeconds(h, m, s),
        speaker: name,
        html,
        // The speaker is part of what you search for — typing "Max" should find
        // his lines, not only the times somebody said his name.
        text: `${name ? `${name} ` : ''}${stripTags(html)}`,
      }
    })
}

/** Rendered HTML → the words in it. Cheap and sufficient: the input is already
 *  sanitized, so there are no scripts or comments to worry about. */
function stripTags(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
}

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Wrap matches in <mark>, without touching markup.
 *
 * Only the text between tags is rewritten, so a search for "a" cannot land
 * inside `<a href>` and corrupt the document. Nothing new is introduced beyond
 * the <mark> itself — the matched text is re-inserted exactly as it was found,
 * so the already-sanitized HTML stays sanitized.
 */
export function highlight(html: string, query: string): string {
  const needle = query.trim()
  if (!needle) return html
  const pattern = new RegExp(escapeRegex(needle), 'gi')
  return html.replace(/>?([^<>]+)(?=<|$)/g, (chunk) =>
    chunk.replace(pattern, (hit) => `<mark>${hit}</mark>`),
  )
}

/** Case-insensitive substring, which is the right tool for one page of text. */
export function lineMatches(line: TranscriptLine, query: string): boolean {
  const needle = query.trim().toLowerCase()
  return !needle || line.text.toLowerCase().includes(needle)
}

/**
 * Which colour each speaker gets, as a slot number the stylesheet maps to a
 * token. The busiest voice takes slot 1 — the brand accent — because on a site
 * named after one person's theories, that voice is the one the page is about.
 * Everyone else follows in order of first appearance, so the assignment is
 * stable for a given transcript and never shifts as you scroll.
 */
export function speakerSlots(lines: TranscriptLine[]): Map<string, number> {
  const order: string[] = []
  const counts = new Map<string, number>()

  for (const line of lines) {
    if (!line.speaker) continue
    if (!counts.has(line.speaker)) order.push(line.speaker)
    counts.set(line.speaker, (counts.get(line.speaker) ?? 0) + 1)
  }

  const ranked = [...order].sort((a, b) => {
    const byCount = (counts.get(b) ?? 0) - (counts.get(a) ?? 0)
    return byCount !== 0 ? byCount : order.indexOf(a) - order.indexOf(b)
  })

  return new Map(ranked.map((name, index) => [name, index + 1]))
}

/**
 * The line the recording is inside at `time` — the last one that has started.
 *
 * Lines without a cue inherit the position of the line above, so a transcript
 * that only stamps every other paragraph still tracks. Returns -1 before the
 * first cue, which reads as "nothing is playing yet" rather than highlighting
 * line one from a standing start.
 */
export function activeLine(lines: TranscriptLine[], time: number): number {
  let found = -1
  for (let i = 0; i < lines.length; i += 1) {
    const at = lines[i].at
    if (at !== null && at <= time) found = i
    else if (at !== null && at > time) break
  }
  return found
}
